import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Check, ChevronDown, ChevronUp, Download, FileCheck2, Loader2, Paperclip, Upload } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { downloadBlob } from "../../lib/downloadBlob";
import { uploadErrorText, uploadFile } from "../../lib/portalUpload";
import { ACCEPT_ATTR } from "../../lib/portalFiles";
import {
  COMPONENTS, DOC_KINDS, FORMS, JUSTIFICATIONS, REPORT_KINDS, STATE_TONES, STATUS_OPTIONS, asciiName,
  componentLine, dayLabel, draftFrom, hours, marksPayload, num, periodPreview, rowProblem, scopeLabel, sessionPath,
  sessionStatus, unmarkedActive, versionLine, whenLabel,
} from "../../lib/portalPresence";
import DatePickerField from "../admin/DatePickerField";
import { Chip } from "./PortalShell";
import { downloadFile } from "./SubmissionHistory";

const card = "rounded-2xl border border-[#E5E7EB] bg-white";
const quiet = "inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-semibold text-[#373A45] hover:border-[#965EA7] hover:text-[#965EA7] disabled:opacity-50";
const primary = "inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50";
const field = "w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#373A45] focus:border-[#965EA7] focus:outline-none focus:ring-2 focus:ring-[#965EA7]/20";
const small = "w-16 rounded-lg border border-[#E5E7EB] px-2 py-1 text-sm tabular-nums focus:border-[#965EA7] focus:outline-none";

/**
 * The live sessions' attendance, everyone's standing per component, and the
 * annexes — the same board for the trainer (`base="/portal"`) and the team
 * (`base="/admin/portal"`, `team`). The server decides and computes; this
 * marks, asks, and shows what it said.
 */
export default function PresenceBoard({ base, groupId, team = false, onOpenParticipant }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      setData((await api.get(`${base}/groups/${groupId}/presence`)).data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Nu am putut încărca prezența."));
    }
  }, [base, groupId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-sm text-[#c0483f]" role="alert">{error}</p>;
  if (!data) return <div className="py-10 text-center text-[#6B7280]"><Loader2 className="mx-auto animate-spin" /></div>;

  return (
    <div className="grid gap-8" data-testid="presence-board">
      <Standing data={data} base={base} groupId={groupId} team={team} onChanged={setData}
        onOpenParticipant={onOpenParticipant} />
      <Sessions data={data} base={base} groupId={groupId} team={team} open={open} setOpen={setOpen} reload={load} />
      <Annexes data={data} base={base} groupId={groupId} team={team} onChanged={setData} />
    </div>
  );
}

// ------------------------------------------------------------------ standing

function Standing({ data, base, groupId, team, onChanged, onOpenParticipant }) {
  const [deciding, setDeciding] = useState(null);
  const c = data.counts || {};
  return (
    <section aria-labelledby="presence-standing">
      <h2 id="presence-standing" className="font-display text-xl font-bold text-[#373A45]">Frecvența pe componente</h2>
      <p className="mt-1 text-sm text-[#6B7280]">
        Procedura cere minimum 80% din timpul fiecărei componente, separat la teoretic și la practic. Peste 20% absențe pe o
        componentă, cursantul e sub prag și nu are drept de depunere a portofoliului până nu decide cineva.
      </p>
      <p className="mt-2 text-sm text-[#373A45]" data-testid="presence-counts">
        {c.ok || 0} în regulă · {c.below || 0} sub prag · {c.recovery || 0} în recuperare · {c.eliminated || 0} eliminați
        {c.abandon ? ` · ${c.abandon} abandon` : ""}
      </p>
      {(data.warnings || []).map((w) => (
        <p key={w} className="mt-2 flex gap-2 rounded-xl bg-[#d99a2b]/10 px-3 py-2 text-sm text-[#8a5d12]">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {w}
        </p>
      ))}
      <div className={`mt-3 overflow-x-auto ${card}`}>
        <table className="w-full min-w-[40rem] text-sm" data-testid="presence-standing-table">
          <thead>
            <tr className="border-b border-[#E5E7EB] text-left text-xs text-[#6B7280]">
              <th className="px-4 py-2.5 font-semibold">Cursant</th>
              <th className="px-3 py-2.5 font-semibold">Teoretic <span className="font-normal">(permis {hours(data.participants[0]?.components.theoretical.allowed_absent ?? 0)})</span></th>
              <th className="px-3 py-2.5 font-semibold">Practic <span className="font-normal">(permis {hours(data.participants[0]?.components.practical.allowed_absent ?? 0)})</span></th>
              <th className="px-3 py-2.5 font-semibold">Stare</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {data.participants.map((p) => (
              <React.Fragment key={p.enrollment_id}>
                <tr className={p.state === "below" ? "bg-[#c0483f]/[0.04]" : ""}>
                  <td className="px-4 py-2.5 font-semibold">
                    {onOpenParticipant
                      ? <button type="button" className="text-left hover:text-[#965EA7]" onClick={() => onOpenParticipant(p.enrollment_id)}>{p.name}</button>
                      : p.name}
                    {p.unmarked_sessions.length > 0 && (
                      <span className="block text-xs font-normal text-[#8a5d12]">
                        {p.unmarked_sessions.length === 1 ? "o sesiune nemarcată" : `${p.unmarked_sessions.length} sesiuni nemarcate`}
                      </span>
                    )}
                  </td>
                  {COMPONENTS.map((comp) => {
                    const x = p.components[comp.value];
                    return (
                      <td key={comp.value} className={`px-3 py-2.5 text-xs ${x.below ? "font-semibold text-[#a33a32]" : "text-[#373A45]"}`}>
                        {componentLine(x)}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2.5"><Chip tone={STATE_TONES[p.state]}>{p.state_label}</Chip></td>
                  <td className="px-4 py-2.5 text-right">
                    {(p.state !== "ok" || p.decision?.state) && (
                      <button type="button" className={quiet} onClick={() => setDeciding(deciding === p.enrollment_id ? null : p.enrollment_id)}
                        data-testid={`presence-decide-${p.enrollment_id}`}>
                        {deciding === p.enrollment_id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                        {p.state === "below" ? "Decide" : "Detalii"}
                      </button>
                    )}
                  </td>
                </tr>
                {deciding === p.enrollment_id && (
                  <tr><td colSpan={5} className="bg-[#FAFAFB] px-4 py-4">
                    <Decision row={p} base={base} groupId={groupId} onChanged={(d) => { onChanged(d); }} />
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {data.participants.length === 0 && <p className="px-4 py-4 text-sm text-[#6B7280]">Niciun cursant în grupă.</p>}
      </div>
      {(data.abandon.length > 0 || data.not_abandon.length > 0) && (
        <div className="mt-3 text-sm text-[#6B7280]" data-testid="presence-abandon">
          {data.abandon.map((a) => (
            <AbandonRow key={a.enrollment_id} a={a} team={team} onDone={async () => onChanged((await api.get(`${base}/groups/${groupId}/presence`)).data)} />
          ))}
          {data.not_abandon.map((a) => (
            <p key={a.enrollment_id}>{a.name}: retras, dar <strong>nu e abandon</strong> — {a.not_abandon?.reason}</p>
          ))}
        </div>
      )}
    </section>
  );
}

function AbandonRow({ a, team, onDone }) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const save = async () => {
    try {
      await api.put(`/admin/portal/enrollments/${a.enrollment_id}/not-abandon`, { value: true, reason });
      toast.success("Am consemnat că nu e abandon.");
      onDone();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva."));
    }
  };
  return (
    <div className="py-1">
      <span><strong className="text-[#373A45]">{a.name}</strong> · abandon (retras pe {whenLabel(a.withdrawn_at)}). Apare așa în anexe.</span>
      {team && !asking && (
        <button type="button" className="ml-2 text-xs font-semibold text-[#965EA7] hover:underline" onClick={() => setAsking(true)}>
          Nu e abandon
        </button>
      )}
      {asking && (
        <div className="mt-2 flex flex-wrap gap-2">
          <input className={`${field} max-w-md`} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="De ce nu e abandon (de exemplu, grupa anulată de noi)" aria-label="Motivul" />
          <button type="button" className={quiet} disabled={!reason.trim()} onClick={save}>Salvează</button>
          <button type="button" className={quiet} onClick={() => setAsking(false)}>Renunță</button>
        </div>
      )}
    </div>
  );
}

function Decision({ row, base, groupId, onChanged }) {
  const d = row.decision || {};
  const [state, setState] = useState(row.can_recover ? "recovery" : "eliminated");
  const [reason, setReason] = useState("");
  const [kind, setKind] = useState("");
  const [verified, setVerified] = useState(false);
  const [plan, setPlan] = useState("");
  const [busy, setBusy] = useState(false);
  const [rec, setRec] = useState({ component: "theoretical", form: "sync", hours: "", date: "", note: "" });

  const send = async (path, body, ok) => {
    setBusy(true);
    try {
      const r = await api.post(`${base}/groups/${groupId}/participants/${row.enrollment_id}/${path}`, body);
      toast.success(ok);
      onChanged(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-4 text-sm text-[#373A45]" data-testid="presence-decision">
      {row.missing.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">Ce lipsește</p>
          <ul className="mt-1 list-disc pl-5 text-xs text-[#4b4f5a]">{row.missing.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}
      {d.state && (
        <p className="rounded-xl bg-white px-3 py-2 text-xs ring-1 ring-[#E5E7EB]">
          Decizia: <strong>{d.state === "recovery" ? "recuperare aprobată" : "eliminat"}</strong>
          {d.justification_kind ? ` (${JUSTIFICATIONS.find((j) => j.value === d.justification_kind)?.label.toLowerCase()}, verificat de ${d.verified_by?.name})` : ""}
          {" "}· {d.decided_by?.name}, {whenLabel(d.decided_at)} · {d.reason}
          {d.remedial_plan && <span className="block">Planul remedial: {d.remedial_plan}</span>}
        </p>
      )}

      {d.state === "recovery" && (
        <div className="grid gap-2">
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">Ore recuperate</p>
          {(d.recoveries || []).map((r) => (
            <p key={r.id} className="text-xs">{dayLabel(r.date)} · {hours(r.hours)} {COMPONENTS.find((c) => c.value === r.component)?.label}, {FORMS.find((f) => f.value === r.form)?.label} · {r.note}</p>
          ))}
          <div className="grid gap-2 sm:grid-cols-[9rem_11rem_6rem_10rem] sm:items-center">
            <select className={field} value={rec.component} onChange={(e) => setRec({ ...rec, component: e.target.value })} aria-label="Componenta">
              {COMPONENTS.map((c) => <option key={c.value} value={c.value}>{c.short}</option>)}
            </select>
            <select className={field} value={rec.form} onChange={(e) => setRec({ ...rec, form: e.target.value })} aria-label="Forma">
              {FORMS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <input className={field} value={rec.hours} inputMode="decimal" placeholder="ore" aria-label="Ore recuperate"
              onChange={(e) => setRec({ ...rec, hours: e.target.value })} />
            <DatePickerField size="sm" value={rec.date} ariaLabel="Data recuperării" onChange={(v) => setRec({ ...rec, date: v })} />
          </div>
          <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-center">
            <input className={field} value={rec.note} placeholder="Ce s-a recuperat (de exemplu, Tema 4 predată)" aria-label="Ce s-a recuperat"
              onChange={(e) => setRec({ ...rec, note: e.target.value })} />
            <button type="button" className={quiet} disabled={busy || !rec.hours || !rec.date || !rec.note.trim()}
              onClick={() => send("recoveries", { ...rec, hours: Number(String(rec.hours).replace(",", ".")) },
                "Am trecut orele recuperate.")}>
              Adaugă
            </button>
          </div>
          <p className="text-xs text-[#6B7280]">
            Cât timp e în recuperare, cursantul nu are drept de depunere. Când orele recuperate aduc absențele la cel mult 20%
            pe fiecare componentă, starea trece singură la „în regulă”.
          </p>
        </div>
      )}

      {row.state !== "ok" && d.state !== "eliminated" && (
        <div className="grid gap-2">
          <div className="flex flex-wrap gap-3">
            {row.can_recover && d.state !== "recovery" && (
              <label className="flex items-center gap-2"><input type="radio" checked={state === "recovery"} onChange={() => setState("recovery")} className="accent-[#965EA7]" /> Aprobă recuperarea</label>
            )}
            <label className="flex items-center gap-2"><input type="radio" checked={state === "eliminated"} onChange={() => setState("eliminated")} className="accent-[#965EA7]" /> Elimină</label>
          </div>
          {!row.can_recover && row.state === "below" && (
            <p className="text-xs text-[#6B7280]">Peste 50% absențe pe o componentă, recuperarea nu mai e posibilă (procedura, art. 17 alin. 3).</p>
          )}
          {state === "recovery" && d.state !== "recovery" && (
            <>
              <select className={`${field} max-w-xs`} value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Motivul absențelor">
                <option value="">Motivul absențelor…</option>
                {JUSTIFICATIONS.map((j) => <option key={j.value} value={j.value}>{j.label}</option>)}
              </select>
              <label className="flex items-start gap-2 text-xs">
                <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} className="mt-0.5 accent-[#965EA7]" />
                <span>Am verificat documentul justificativ. <strong>Nu îl încarc în portal</strong>: rămâne la furnizor (date despre sănătate, GDPR art. 9).</span>
              </label>
              <textarea className={field} rows={2} value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="Planul remedial: ce recuperează, cu cine și până când" aria-label="Planul remedial" />
            </>
          )}
          {!(state === "recovery" && d.state === "recovery") && (
            <>
              <textarea className={field} rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder={state === "recovery" ? "Motivul, pe scurt" : "De ce: de exemplu, 1,5 ore absențe din 6 la componenta teoretică"} aria-label="Motivul" />
              <div>
                <button type="button" className={primary} disabled={busy || !reason.trim()}
                  onClick={() => send("standing", { state, reason, justification_kind: kind, verified, remedial_plan: plan },
                    state === "recovery" ? "Recuperarea e aprobată." : "Am consemnat eliminarea.")}
                  data-testid="presence-decision-save">
                  {busy && <Loader2 size={14} className="animate-spin" />} {state === "recovery" ? "Aprobă recuperarea" : "Consemnează eliminarea"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {d.state && (
        <UndoDecision onUndo={(why) => send("standing", { state: null, reason: why }, "Decizia e anulată.")} busy={busy} />
      )}
    </div>
  );
}

function UndoDecision({ onUndo, busy }) {
  const [why, setWhy] = useState("");
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return <button type="button" className="w-fit text-xs font-semibold text-[#6B7280] hover:text-[#a33a32]" onClick={() => setAsking(true)}>Anulează decizia</button>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      <input className={`${field} max-w-md`} value={why} onChange={(e) => setWhy(e.target.value)} placeholder="De ce anulezi decizia" aria-label="De ce anulezi decizia" />
      <button type="button" className={quiet} disabled={busy || !why.trim()} onClick={() => onUndo(why)}>Anulează decizia</button>
    </div>
  );
}

// ------------------------------------------------------------------ sessions

function Sessions({ data, base, groupId, team, open, setOpen, reload }) {
  return (
    <section aria-labelledby="presence-sessions">
      <h2 id="presence-sessions" className="font-display text-xl font-bold text-[#373A45]">Sesiunile live</h2>
      <p className="mt-1 text-sm text-[#6B7280]">
        Marchează fiecare sesiune după ce s-a ținut, atașează raportul de prezență (exportul din Meet sau foaia voastră) și
        confirm-o. După confirmare, o schimbare cere un motiv și rămâne consemnată.
      </p>
      <ul className={`mt-3 divide-y divide-[#E5E7EB] ${card}`} data-testid="presence-sessions">
        {data.sessions.map((s, i) => {
          const st = sessionStatus(s);
          const isOpen = open === s.key;
          return (
            <li key={s.key}>
              <button type="button" disabled={!s.held} onClick={() => setOpen(isOpen ? null : s.key)}
                className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-[#F5F6F6]/70 disabled:cursor-default disabled:hover:bg-transparent"
                data-testid={`presence-session-${i}`}>
                <span className="w-6 text-xs tabular-nums text-[#6B7280]">{i + 1}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-[#373A45]">{dayLabel(s.date)} · {s.start_time}–{s.end_time}</span>
                  <span className="block text-xs text-[#6B7280]">
                    teoretic {num(s.theory)} h · practic {num(s.practice)} h
                    {s.themes.length > 0 && ` · temele ${s.themes.join(", ")}`}
                    {s.reports.length > 0 && ` · ${s.reports.length === 1 ? "un raport" : `${s.reports.length} rapoarte`}`}
                  </span>
                </span>
                <Chip tone={st.tone}>{st.label}</Chip>
                {s.held && (isOpen ? <ChevronUp size={16} className="text-[#9CA3AF]" /> : <ChevronDown size={16} className="text-[#9CA3AF]" />)}
              </button>
              {isOpen && <SessionEditor base={base} groupId={groupId} sessionKey={s.key} team={team} onSaved={reload} />}
            </li>
          );
        })}
        {data.sessions.length === 0 && <li className="px-4 py-4 text-sm text-[#6B7280]">Grupa nu are sesiuni.</li>}
      </ul>
    </section>
  );
}

function SessionEditor({ base, groupId, sessionKey, team, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState([]);
  const [reason, setReason] = useState("");
  const [kind, setKind] = useState("");
  const [camera, setCamera] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const path = sessionPath(base, groupId, sessionKey);

  const apply = (d) => { setDetail(d); setDraft(draftFrom(d)); setKind(d.attendance?.report_kind || ""); };
  useEffect(() => {
    api.get(path).then((r) => apply(r.data)).catch((err) => toast.error(apiErrorMessage(err, "Nu am putut deschide sesiunea.")));
  }, [path]);

  if (!detail) return <div className="px-4 pb-4"><Loader2 className="animate-spin text-[#6B7280]" /></div>;
  const s = detail.session;
  const confirmed = Boolean(detail.attendance?.confirmed_at);
  const problems = draft.map((r) => rowProblem(r, s));
  const set = (i, patch) => setDraft((rows) => rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const everyone = (status) => setDraft((rows) => rows.map((r) => (r.abandon ? r : { ...r, status })));

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(path, { marks: marksPayload(draft), reason });
      apply(r.data);
      setReason("");
      toast.success(confirmed ? "Am salvat corectura." : "Am salvat prezența.");
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva prezența."));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${path}/confirm`, { camera_confirmed: camera, report_kind: kind });
      apply(r.data);
      toast.success("Sesiunea e confirmată.");
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut confirma sesiunea."));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await uploadFile(`${path}/reports/uploads`, file, { completeBase: team ? "/admin/portal/files" : "/portal/files" });
      apply((await api.get(path)).data);
      toast.success("Raportul e atașat.");
      onSaved();
    } catch (err) {
      toast.error(uploadErrorText(err, apiErrorMessage));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = (f) => downloadFile(f.id, (msg) => toast.error(msg), team ? "/admin/portal" : "/portal");

  return (
    <div className="grid gap-4 border-t border-[#E5E7EB] bg-[#FAFAFB] px-4 py-4" data-testid="presence-session-editor">
      {confirmed && (
        <p className="flex items-start gap-2 rounded-xl bg-[#519872]/10 px-3 py-2 text-xs text-[#3c7457]">
          <FileCheck2 size={14} className="mt-0.5 shrink-0" />
          Confirmată de {detail.attendance.confirmed_by?.name} la {whenLabel(detail.attendance.confirmed_at)} · raport{" "}
          {detail.labels.reports[detail.attendance.report_kind]}.
          {(detail.attendance.corrections || []).length > 0 && ` ${detail.attendance.corrections.length} corecturi consemnate.`}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-[#6B7280]">Toți:</span>
        <button type="button" className={quiet} onClick={() => everyone("present")}>prezenți</button>
        <span className="text-[#6B7280]">Sesiunea are {num(s.theory)} h teoretic și {num(s.practice)} h practic.</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[34rem] text-sm">
          <thead>
            <tr className="text-left text-xs text-[#6B7280]">
              <th className="py-1.5 pr-3 font-semibold">Cursant</th>
              <th className="py-1.5 pr-3 font-semibold">Prezența</th>
              <th className="py-1.5 font-semibold">Ore lipsă (la parțial)</th>
            </tr>
          </thead>
          <tbody>
            {draft.map((r, i) => (
              <tr key={r.enrollment_id} className="align-top">
                <td className="py-1.5 pr-3 font-semibold text-[#373A45]">
                  {r.name}{r.abandon && <span className="ml-1 text-xs font-normal text-[#6B7280]">(retras)</span>}
                </td>
                <td className="py-1.5 pr-3">
                  <div className="inline-flex overflow-hidden rounded-full border border-[#E5E7EB] bg-white" role="radiogroup" aria-label={`Prezența lui ${r.name}`}>
                    {STATUS_OPTIONS.map((o) => (
                      <button key={o.value} type="button" role="radio" aria-checked={r.status === o.value}
                        onClick={() => set(i, { status: o.value })}
                        className={`px-3 py-1 text-xs font-semibold ${r.status === o.value
                          ? o.value === "absent" ? "bg-[#c0483f] text-white" : o.value === "partial" ? "bg-[#d99a2b] text-white" : "bg-[#519872] text-white"
                          : "text-[#373A45] hover:bg-[#F5F6F6]"}`}
                        data-testid={`presence-${o.value}-${i}`}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                </td>
                <td className="py-1.5">
                  {r.status === "partial" && (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-[#373A45]">
                      {s.theory > 0 && (
                        <label className="inline-flex items-center gap-1">teoretic
                          <input className={small} inputMode="decimal" value={r.theoretical} onChange={(e) => set(i, { theoretical: e.target.value })} aria-label={`Ore teoretice lipsă, ${r.name}`} /> h
                        </label>
                      )}
                      {s.practice > 0 && (
                        <label className="inline-flex items-center gap-1">practic
                          <input className={small} inputMode="decimal" value={r.practical} onChange={(e) => set(i, { practical: e.target.value })} aria-label={`Ore practice lipsă, ${r.name}`} /> h
                        </label>
                      )}
                      {problems[i] && <span className="font-semibold text-[#a33a32]">{problems[i]}</span>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {confirmed && (
        <textarea className={field} rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Sesiunea e confirmată: scrie de ce schimbi prezența" aria-label="Motivul corecturii" />
      )}
      <div>
        <button type="button" className={primary} onClick={save}
          disabled={busy || problems.some(Boolean) || (confirmed && !reason.trim())} data-testid="presence-save">
          {busy && <Loader2 size={14} className="animate-spin" />} {confirmed ? "Salvează corectura" : "Salvează prezența"}
        </button>
      </div>

      <div className="grid gap-2 rounded-xl bg-white p-3 ring-1 ring-[#E5E7EB]">
        <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">Raportul de prezență</p>
        {detail.reports.map((f) => (
          <button key={f.id} type="button" onClick={() => download(f)} className="inline-flex w-fit items-center gap-1.5 text-sm text-[#965EA7] hover:underline">
            <Paperclip size={13} /> {f.name}
          </button>
        ))}
        <div>
          <input ref={fileRef} type="file" accept={ACCEPT_ATTR} className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          <button type="button" className={quiet} disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="presence-report-upload">
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Atașează raportul
          </button>
        </div>
        {!confirmed && (
          <>
            <div className="grid gap-1 text-sm">
              {REPORT_KINDS.map((k) => (
                <label key={k.value} className="flex items-center gap-2">
                  <input type="radio" name={`kind-${sessionKey}`} checked={kind === k.value} onChange={() => setKind(k.value)} className="accent-[#965EA7]" />
                  {k.label}
                </label>
              ))}
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={camera} onChange={(e) => setCamera(e.target.checked)} className="mt-0.5 accent-[#965EA7]" />
              <span>Cei marcați prezenți au avut camera deschisă (procedura, art. 18 lit. b).</span>
            </label>
            <div>
              <button type="button" className={primary} onClick={confirm} data-testid="presence-confirm"
                disabled={busy || !camera || !kind || detail.reports.length === 0 || unmarkedActive(draft) > 0}>
                <Check size={14} /> Confirmă sesiunea
              </button>
              {unmarkedActive(draft) > 0 && <span className="ml-2 text-xs text-[#6B7280]">Marchează întâi pe toți și salvează.</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ annexes

function Annexes({ data, base, groupId, team, onChanged }) {
  const [day, setDay] = useState("");
  const [registry, setRegistry] = useState({ no: "", date: "" });
  const [busy, setBusy] = useState("");
  const docs = data.documents || [];

  const periods = data.annex_4_3?.periods || [];
  const generate = async (kind, period = null) => {
    setBusy(period ? "annex_4_3_period" : kind);
    try {
      const r = await api.post(`${base}/groups/${groupId}/documents`, {
        kind, day: kind === "annex_4_2" ? day : "", registry_no: registry.no, registry_date: registry.date,
        ...(period || {}) });
      toast.success(`${DOC_KINDS.find((k) => k.value === kind).label}, versiunea ${r.data.document.version}, e generată.`);
      onChanged(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut genera documentul."));
    } finally {
      setBusy("");
    }
  };

  const ready = { annex_4_2: Boolean(day), annex_4_3: true, annex_5: data.annex_5.ready };
  return (
    <section aria-labelledby="presence-annexes">
      <h2 id="presence-annexes" className="font-display text-xl font-bold text-[#373A45]">Anexele</h2>
      <p className="mt-1 text-sm text-[#6B7280]">
        Un document generat nu se mai schimbă: o corectură ulterioară înseamnă o versiune nouă, lângă cea veche. Anexa 5
        certifică finalizarea formării; din ea se citește dreptul de depunere a portofoliului.
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr] sm:max-w-md">
        <input className={field} value={registry.no} onChange={(e) => setRegistry({ ...registry, no: e.target.value })}
          placeholder="Nr. de înregistrare (opțional)" aria-label="Numărul din registru" />
        <input className={field} value={registry.date} onChange={(e) => setRegistry({ ...registry, date: e.target.value })}
          placeholder="din data (opțional)" aria-label="Data din registru" />
      </div>
      <div className={`mt-3 divide-y divide-[#E5E7EB] ${card}`} data-testid="presence-annexes">
        {DOC_KINDS.map((k) => {
          const versions = docs.filter((d) => d.kind === k.value);
          return (
            <div key={k.value} className="grid gap-2 px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm"><strong className="text-[#373A45]">{k.label}</strong> <span className="text-[#6B7280]">· {k.title}</span></p>
                <div className="flex flex-wrap items-center gap-2">
                  {k.perDay && (
                    <select className={`${field} w-44 py-1.5`} value={day} onChange={(e) => setDay(e.target.value)} aria-label="Ziua de formare">
                      <option value="">Ziua…</option>
                      {data.annex_4_2_days.map((d) => <option key={d} value={d}>{dayLabel(d)}</option>)}
                    </select>
                  )}
                  <button type="button" className={quiet} disabled={!ready[k.value] || busy === k.value} onClick={() => generate(k.value)}
                    data-testid={`presence-generate-${k.value}`}>
                    {busy === k.value && <Loader2 size={13} className="animate-spin" />} Generează
                  </button>
                </div>
              </div>
              {k.value === "annex_5" && !data.annex_5.ready && (
                <ul className="list-disc pl-5 text-xs text-[#8a5d12]" data-testid="presence-annex5-reasons">
                  {data.annex_5.reasons.map((r) => <li key={r}>{r}</li>)}
                </ul>
              )}
              {k.value === "annex_4_3" && data.annex_4_3 && (
                <AsyncPeriod annex={data.annex_4_3} busy={busy === "annex_4_3_period"}
                  onGenerate={(start, end) => generate("annex_4_3", { start, end })} />
              )}
              {k.perDay && data.annex_4_2_days.length === 0 && (
                <p className="text-xs text-[#6B7280]">Se poate genera pentru o zi după ce toate sesiunile din ea sunt confirmate.</p>
              )}
              {versions.map((d) => (
                <div key={d.id} className="flex flex-wrap items-center gap-2 text-xs text-[#373A45]">
                  <span className={d.latest ? "font-semibold" : "text-[#6B7280]"}>
                    {d.scope ? `${scopeLabel(d, periods)} · ` : (k.value === "annex_4_3" ? "Tot cursul · " : "")}{versionLine(d)}{d.registry_no ? ` · nr. ${d.registry_no}` : ""}
                  </span>
                  {d.signed && <Chip tone="good">semnată</Chip>}
                  {d.stale && <Chip tone="warn">datele s-au schimbat după această versiune</Chip>}
                  {["pdf", "xlsx"].map((fmt) => (
                    <button key={fmt} type="button" className="inline-flex items-center gap-1 font-semibold text-[#965EA7] hover:underline"
                      onClick={() => downloadBlob(`${base}/documents/${d.id}/${fmt}`,
                        `${k.label.replace(/ /g, "_")}_${asciiName(data.group.name)}${d.scope ? `_${d.scope}` : ""}_v${d.version}.${fmt}`)}>
                      <Download size={12} /> {fmt === "pdf" ? "PDF" : "Excel"}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {team && <AnnexGroupFields data={data} groupId={groupId} />}
    </section>
  );
}

/**
 * Anexa 4.3 for one period (T-3): the model's "perioada/perioadele". The
 * report holds the activities due in the period; nothing blocks, it only
 * warns. The whole-course report above stays as it was.
 */
function AsyncPeriod({ annex, busy, onGenerate }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const preview = periodPreview(annex, start, end);
  const left = Math.round((annex.total_hours - annex.reported_hours) * 100) / 100;
  return (
    <div className="grid gap-2 rounded-xl bg-[#F7F5F9] px-3 py-3 text-xs text-[#373A45]" data-testid="presence-annex43-period">
      <p>
        <strong>Pe o perioadă.</strong> „Generează” de mai sus face raportul pe tot cursul. Aici faci rapoarte periodice: alegi
        perioada, iar raportul cuprinde temele cu termenul în ea, cu orele dovedite pe ele.
      </p>
      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <div>
          <span className="text-[#6B7280]">De la</span>
          <DatePickerField size="sm" value={start} ariaLabel="Începutul perioadei" onChange={setStart} testid="annex43-start" />
        </div>
        <div>
          <span className="text-[#6B7280]">Până la</span>
          <DatePickerField size="sm" value={end} ariaLabel="Sfârșitul perioadei" onChange={setEnd} minDate={start || undefined}
            testid="annex43-end" />
        </div>
        <button type="button" className={`${quiet} justify-center`} disabled={!preview || preview.activities.length === 0 || busy}
          onClick={() => onGenerate(start, end)} data-testid="presence-generate-annex_4_3-period">
          {busy && <Loader2 size={13} className="animate-spin" />} Generează pentru perioadă
        </button>
      </div>
      {preview && (preview.activities.length === 0 ? (
        <p className="text-[#6B7280]">În perioada aleasă nu e nicio temă cu termen.</p>
      ) : (
        <p data-testid="annex43-preview">
          {preview.activities.length === 1 ? "O temă" : `${preview.activities.length} teme`}, {hours(preview.hours)}:{" "}
          {preview.activities.map((a) => a.title).join(", ")}.
        </p>
      ))}
      {preview?.alreadyIn.length > 0 && (
        <p className="text-[#8a5d12]">
          Apar deja în alt raport: {preview.alreadyIn.map((a) => `${a.title} (raportul ${a.number})`).join(", ")}. Se poate genera
          oricum, dar aceleași ore vor fi în două rapoarte.
        </p>
      )}
      {preview?.notDue.length > 0 && (
        <p className="text-[#8a5d12]">
          Termenul n-a trecut încă la: {preview.notDue.join(", ")}. Cine n-a predat apare acum fără ore, deși mai poate preda.
        </p>
      )}
      {annex.periods.length > 0 && (
        <p className="text-[#6B7280]" data-testid="annex43-coverage">
          Pe perioade: {hours(annex.reported_hours)} din {hours(annex.total_hours)} raportate
          {left > 0 && annex.missing.length > 0 ? `; rămân: ${annex.missing.join(", ")}` : ""}.
        </p>
      )}
      {annex.activities.some((a) => !a.due_day) && (
        <p className="text-[#6B7280]">
          Fără termen, deci nu intră în nicio perioadă: {annex.activities.filter((a) => !a.due_day).map((a) => a.title).join(", ")}.
        </p>
      )}
    </div>
  );
}

function AnnexGroupFields({ data, groupId }) {
  const [form, setForm] = useState({
    series: "", group_no: "", evaluation_date: "", evaluation_form: "", evaluation_place: "", evaluation_start: "",
    evaluation_end: "", ...(data.group.annex || {}) });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/portal/groups/${groupId}/annex`, form);
      toast.success("Am salvat datele grupei pentru anexe.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva."));
    } finally {
      setBusy(false);
    }
  };
  const input = (key, label, placeholder) => (
    <label className="text-xs text-[#6B7280]">{label}
      <input className={`${field} mt-1`} value={form[key] || ""} placeholder={placeholder}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
    </label>
  );
  return (
    <div className={`mt-4 grid gap-3 p-4 ${card}`} data-testid="presence-annex-fields">
      <p className="text-sm font-semibold text-[#373A45]">Datele grupei din antetul anexelor (câmpurile 11–16)</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {input("series", "Seria", "de exemplu, 1")}
        {input("group_no", "Grupa", data.group.name)}
        <label className="text-xs text-[#6B7280]">Data evaluării finale
          <div className="mt-1"><DatePickerField size="sm" value={form.evaluation_date || ""} ariaLabel="Data evaluării finale"
            onChange={(v) => setForm({ ...form, evaluation_date: v })} /></div>
        </label>
        {input("evaluation_form", "Forma evaluării finale", "online sincron")}
        {input("evaluation_place", "Platforma evaluării finale", "Google Meet")}
        <div className="grid grid-cols-2 gap-2">
          {input("evaluation_start", "Începe la", "17:00")}
          {input("evaluation_end", "Se termină la (cel târziu 20:00)", "19:30")}
        </div>
      </div>
      <div><button type="button" className={quiet} disabled={busy} onClick={save}>Salvează datele grupei</button></div>
      <p className="text-xs text-[#6B7280]">Se aplică documentelor generate de acum înainte; cele generate rămân cum sunt.</p>
    </div>
  );
}
